import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"
import { Evaluate, getAddress } from "viem"
import {
  type GetVaultKinkInterestRateModelInfoErrorType,
  type GetVaultKinkInterestRateModelInfoParameters,
  type GetVaultKinkInterestRateModelInfoReturnType,
  getVaultKinkInterestRateModelInfo,
} from "../actions/getVaultKinkInterestRateModelInfo"
import type { ExtendedConfig, ScopeKeyParameter } from "../types"
import { filterQueryOptions } from "../utils"

export type GetVaultKinkInterestRateModelInfoOptions<
  config extends ExtendedConfig = ExtendedConfig,
> = Evaluate<
  ExactPartial<GetVaultKinkInterestRateModelInfoParameters<config>> &
    ScopeKeyParameter
>

export function getVaultKinkInterestRateModelInfoQueryKey<
  config extends ExtendedConfig = ExtendedConfig,
>(options: GetVaultKinkInterestRateModelInfoOptions<config> = {}) {
  const { address, evcAddress, vaultLensAddress, ...parameters } = options
  return [
    "evcVaultKinkInterestRateModelInfo",
    filterQueryOptions({
      ...parameters,
      address: address && getAddress(address),
      evcAddress: evcAddress && getAddress(evcAddress),
      vaultLensAddress: vaultLensAddress && getAddress(vaultLensAddress),
    }),
  ] as const
}

export function getVaultKinkInterestRateModelInfoQueryOptions<
  config extends ExtendedConfig,
>(config: config, options: GetVaultKinkInterestRateModelInfoOptions<config>) {
  return {
    gcTime: 0,
    async queryFn({ queryKey }) {
      return getVaultKinkInterestRateModelInfoQueryFn(config, queryKey[1])
    },
    queryKey: getVaultKinkInterestRateModelInfoQueryKey(options),
  } satisfies QueryOptions<
    GetVaultKinkInterestRateModelInfoQueryFnData,
    GetVaultKinkInterestRateModelInfoErrorType,
    GetVaultKinkInterestRateModelInfoData,
    GetVaultKinkInterestRateModelInfoQueryKey
  >
}

export function getVaultKinkInterestRateModelInfoQueryFn(
  config: ExtendedConfig,
  options: GetVaultKinkInterestRateModelInfoOptions = {},
) {
  const { address, ...parameters } = options
  return getVaultKinkInterestRateModelInfo(config, {
    ...parameters,
    address: address && getAddress(address),
  })
}

export type GetVaultKinkInterestRateModelInfoQueryFnData =
  GetVaultKinkInterestRateModelInfoReturnType

export type GetVaultKinkInterestRateModelInfoData =
  GetVaultKinkInterestRateModelInfoQueryFnData

export type GetVaultKinkInterestRateModelInfoQueryKey = ReturnType<
  typeof getVaultKinkInterestRateModelInfoQueryKey
>
